import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import { Button } from './ui/button';
import { Tag, ChevronDown, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fixed list of collaborators that can be assigned to a dossier
export const ASSIGNABLE_COLLABORATORS = [
  'Sabrina', 'Harout', 'Ivan', 'Azdine', 'Sasha', 'Pierre', 'Evan', 'Quentin'
];

// Color per collaborator (Tailwind classes) for visual distinction
const COLLAB_COLORS = {
  Sabrina:  'bg-pink-600 hover:bg-pink-700 text-white',
  Harout:   'bg-blue-600 hover:bg-blue-700 text-white',
  Ivan:     'bg-cyan-600 hover:bg-cyan-700 text-white',
  Azdine:   'bg-red-600 hover:bg-red-700 text-white',
  Sasha:    'bg-purple-600 hover:bg-purple-700 text-white',
  Pierre:   'bg-amber-600 hover:bg-amber-700 text-white',
  Evan:     'bg-emerald-600 hover:bg-emerald-700 text-white',
  Quentin:  'bg-indigo-600 hover:bg-indigo-700 text-white',
};

/**
 * Reusable assignment tag for any dossier type.
 *
 * Props:
 * - dossierType : "appointment" | "wash" | "towing" | "quote" | "admin_quote" | "invoice" | "order" | "contact"
 * - dossierId   : id of the document
 * - assignedTo  : current value (collaborator name or null)
 * - onChange    : optional callback(newValue) after a successful update
 * - compact     : show a compact badge (smaller)
 */
export default function DossierAssignmentTag({
  dossierType,
  dossierId,
  assignedTo,
  onChange,
  compact = false,
}) {
  const [current, setCurrent] = useState(assignedTo || null);
  const [saving, setSaving] = useState(false);

  const updateAssignment = async (newValue) => {
    if (saving) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/admin/dossiers/${dossierType}/${dossierId}/assign`,
        { assigned_to: newValue },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCurrent(newValue);
      if (onChange) onChange(newValue);
      toast.success(newValue ? `Attribué à ${newValue}` : 'Étiquette retirée');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'attribution");
    } finally {
      setSaving(false);
    }
  };

  const colorClass = current ? COLLAB_COLORS[current] || 'bg-zinc-700 text-white' : 'bg-zinc-800 text-zinc-400 border border-dashed border-zinc-600 hover:bg-zinc-700';
  const sizeClass = compact ? 'h-7 px-2 text-xs' : 'h-8 px-3 text-sm';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          className={`${colorClass} ${sizeClass} font-semibold rounded-full transition-all gap-1`}
          disabled={saving}
          data-testid={`assign-tag-${dossierType}-${dossierId}`}
        >
          <Tag size={compact ? 11 : 13} />
          {current || 'Attribuer'}
          <ChevronDown size={11} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-zinc-900 border-zinc-700">
        <DropdownMenuLabel className="text-zinc-400 text-xs">Choisir un collaborateur</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-zinc-700" />
        {ASSIGNABLE_COLLABORATORS.map((name) => (
          <DropdownMenuItem
            key={name}
            onClick={() => updateAssignment(name)}
            className={`cursor-pointer ${current === name ? 'bg-zinc-800' : ''}`}
            data-testid={`assign-option-${name}`}
          >
            <span className={`inline-block w-2 h-2 rounded-full mr-2 ${COLLAB_COLORS[name]?.split(' ')[0] || 'bg-zinc-500'}`} />
            <span className="text-white">{name}</span>
            {current === name && <span className="ml-auto text-green-500 text-xs">✓</span>}
          </DropdownMenuItem>
        ))}
        {current && (
          <>
            <DropdownMenuSeparator className="bg-zinc-700" />
            <DropdownMenuItem
              onClick={() => updateAssignment(null)}
              className="cursor-pointer text-red-400"
              data-testid="assign-option-remove"
            >
              <X size={14} className="mr-2" />
              Retirer l&apos;étiquette
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
